import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Dialog,
  Toast,
} from 'antd-mobile';
import {
  UserOutline,
  PhoneFill,
  RightOutline,
  SendOutline,
  TeamOutline,
  EditSOutline,
} from 'antd-mobile-icons';
import { useAuthStore } from '@/stores/authStore';
import { getRoleText } from '@/utils/formatters';

function QuickItem({ icon, label, onClick }: {
  icon: string; label: string; onClick?: () => void;
}) {
  return (
    <div className="quick-item" onClick={onClick}>
      <span className="quick-icon">{icon}</span>
      <span className="quick-label">{label}</span>
    </div>
  );
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();

  const handleLogout = () => {
    Dialog.confirm({
      title: '确认退出',
      content: '退出后将需要重新登录',
      confirmText: '退出',
      cancelText: '取消',
      onConfirm: () => {
        clearAuth();
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        Toast.show({ icon: 'success', content: '已退出登录' });
        navigate('/login', { replace: true });
      },
    });
  };

  // 获取角色颜色
  const getRoleColor = (role: string) => {
    const map: Record<string, string> = {
      admin: '#FF4D4F',
      inspector: '#2E5BFF',
      responder: '#52C41A',
    };
    return map[role] || '#9CA3AF';
  };

  // 获取角色图标
  const getRoleIcon = (role: string) => {
    if (role === 'admin') return <SendOutline />;
    if (role === 'inspector') return <EditSOutline />;
    return <TeamOutline />;
  };

  return (
    <div className="profile-page">
      {/* 用户信息卡片 */}
      <div className="profile-user-card">
        <div className="profile-user-row">
          <div className="profile-avatar-wrap">
            <img
              src="/avatar.png"
              alt="avatar"
              className="profile-avatar-img"
            />
          </div>
          <div className="profile-user-info">
            <div className="profile-name-row">
              <span className="profile-name">
                {user?.real_name || '未登录'}
              </span>
              <span
                className="profile-role-tag"
                style={{
                  background: `${getRoleColor(user?.role || '')}15`,
                  color: getRoleColor(user?.role || ''),
                  border: `1px solid ${getRoleColor(user?.role || '')}30`,
                }}
              >
                <span className="role-icon">{getRoleIcon(user?.role || '')}</span>
                <span>{getRoleText(user?.role || '')}</span>
              </span>
            </div>
            <div className="profile-username">
              @{user?.username || 'guest'}
            </div>
          </div>
        </div>
      </div>

      <div className="profile-content">
        <div className="info-card">
          <div className="info-card-title">基本信息</div>
          <div className="info-row">
            <UserOutline fontSize={18} color="#2E5BFF" />
            <span className="info-label">用户名</span>
            <span className="info-value">{user?.username || '-'}</span>
          </div>
          <div className="info-row">
            <SendOutline fontSize={18} color="#FA8C16" />
            <span className="info-label">用户角色</span>
            <span className="info-value">{getRoleText(user?.role || '')}</span>
          </div>
          <div className="info-row last">
            <PhoneFill fontSize={18} color="#52C41A" />
            <span className="info-label">手机号码</span>
            <span className="info-value">{user?.phone || '未设置'}</span>
          </div>
        </div>

        <Card className="profile-card" title="快捷入口">
          <div className="quick-grid">
            <QuickItem icon="📋" label="我的工单" onClick={() => navigate('/tickets')} />
            <QuickItem icon="⏰" label="待办事项" onClick={() => navigate('/tickets?status=pending')} />
            <QuickItem icon="✅" label="完成整改" onClick={() => navigate('/tickets?status=rectified')} />
            <QuickItem icon="⚙️" label="修改密码" onClick={() => navigate('/settings')} />
          </div>
        </Card>


        <div className="logout-section">
          <Button
            block
            fill="outline"
            size="large"
            onClick={handleLogout}
            className="logout-btn"
          >
            <span>退出登录</span>
          </Button>
        </div>

        <div className="profile-footer">
          <p>现场智管 · 让现场管理更智能</p>
        </div>
      </div>

      <style>{`
        .profile-page {
          min-height: 100vh;
          background: #F5F7FA;
          padding-bottom: calc(16px + env(safe-area-inset-bottom));
        }

        /* 顶部标题条 */
        .profile-title-header {
          background: #2E5BFF;
          color: #fff;
          padding: 16px 16px 14px;
          text-align: center;
          font-size: 18px;
          font-weight: 600;
          letter-spacing: 2px;
          box-shadow: 0 2px 8px rgba(30, 63, 204, 0.15);
          position: sticky;
          top: 0;
          z-index: 100;
        }

        /* 用户信息白卡片 */
        .profile-user-card {
          background: #fff;
          margin: 10px 12px 0;
          border-radius: 12px;
          padding: 16px;
          border: 1px solid #E8ECF3;
          box-shadow: 0 2px 12px rgba(30, 63, 204, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
        }

        .profile-user-row {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .profile-avatar-wrap {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          overflow: hidden;
          flex-shrink: 0;
          border: 2px solid #2E5BFF30;
          background: #F5F7FA;
        }

        .profile-avatar-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .profile-user-info {
          flex: 1;
          min-width: 0;
        }

        .profile-name-row {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;
          flex-wrap: wrap;
        }

        .profile-name {
          font-size: 17px;
          font-weight: 600;
          color: #1F2433;
        }

        .profile-role-tag {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 10px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 500;
          line-height: 1.4;
          height: 22px;
        }

        .role-icon {
          display: inline-flex;
          align-items: center;
        }

        .profile-username {
          font-size: 12px;
          color: #9CA3AF;
          margin-left: 2px;
        }

                .profile-content {
          padding: 0;
        }

        .info-card {
          background: #fff;
          margin: 10px 12px;
          border-radius: 12px;
          border: 1px solid #E8ECF3;
          box-shadow: 0 2px 12px rgba(30, 63, 204, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
          overflow: hidden;
        }

        .info-card-title {
          font-size: 14px;
          font-weight: 600;
          color: #1F2433;
          padding: 14px 16px 12px 16px;
        }

        .info-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          border-bottom: 1px solid #F0F2F5;
        }

        .info-row.last {
          border-bottom: none;
        }

        .info-label {
          flex: 1;
          font-size: 14px;
          color: #1F2433;
        }

        .info-value {
          font-size: 13px;
          color: #6B7280;
        }

        .profile-card {
          margin: 0 12px 10px;
          border-radius: 12px;
          box-shadow: 0 2px 12px rgba(30, 63, 204, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
          border: 1px solid #E8ECF3;
        }



        .profile-list::after { display: none !important; border: none !important; }

        .profile-card .adm-list-item:last-child { border-bottom: none !important; }




        .quick-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0;
          padding: 8px 0 12px;
        }

        .quick-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
          padding: 4px 2px;
        }

        .quick-item:active {
          transform: scale(0.92);
        }

        .quick-icon {
          font-size: 26px;
          line-height: 1;
        }

        .quick-label {
          font-size: 11px;
          color: #1F2433;
          font-weight: 500;
          white-space: nowrap;
        }

        .logout-section {
          margin: 8px 12px 16px;
        }

        .logout-btn {
          background: white;
          color: #FF4D4F;
          border-color: #FF4D4F !important;
          border-radius: 10px;
        }

        .profile-footer {
          text-align: center;
          color: #9CA3AF;
          font-size: 11px;
          padding: 12px 0 8px;
        }

        .profile-footer p {
          margin: 0;
        }
      `}</style>
    </div>
  );
}
